import { api } from './api';
import type {
  ApiEnvelope, AuditFields, EngineListParams, PaginatedEnvelope,
} from './notification-engine.types';

const BASE = '/api/v1/schedules';

/**
 * A schedule_master row — when something fires.
 *
 * One collection for every kind of scheduled work: `masterType` says what the
 * schedule points at (`NOTIFICATION`, `BACKGROUND_JOB`) and `masterId` names
 * the row. One poller reads it, which is what makes double-firing structurally
 * impossible rather than merely unlikely.
 */
export interface Schedule extends AuditFields {
  _id: string;
  scheduleCode: string;
  masterType: string;               // NOTIFICATION | BACKGROUND_JOB
  masterId: string;
  scheduleType: string;             // CRON | INTERVAL | ONCE | EVENT
  // Exactly one of these applies, decided by scheduleType. They are top-level
  // fields the scheduler reads directly — not part of typeConfig, which neither
  // target declares a single valid key for.
  cronExpression?: string | null;
  intervalSeconds?: number | null;
  runAtDateTime?: string | null;
  typeConfig?: Record<string, unknown> | null;
  parameters?: Record<string, unknown> | null;
  eventKey?: string | null;
  /** IANA zone the CRON expression is read in; the scheduler falls back to IST. */
  timezone?: string | null;
  isScheduleEnabled?: boolean;
  nextRunAt?: string | null;
  lastRunAt?: string | null;
  maxOccurrences?: number | null;
  occurrenceCount?: number | null;
}

export type ScheduleCreateRequest =
  Omit<Schedule, '_id' | 'nextRunAt' | 'lastRunAt' | 'occurrenceCount' | keyof AuditFields>
  & { appId?: string | null };
export type ScheduleUpdateRequest = Partial<ScheduleCreateRequest>;

export interface SchedulePreview {
  /** The next fire times this configuration produces, without saving it. */
  runs?: string[];
  [key: string]: unknown;
}

export interface ScheduleListParams extends EngineListParams {
  masterType?: string;
}

export const schedulesService = {
  list: async (params?: ScheduleListParams): Promise<PaginatedEnvelope<Schedule>> => {
    const response = await api.get<PaginatedEnvelope<Schedule>>(BASE, { params });
    return response.data;
  },
  getById: async (id: string): Promise<Schedule> => {
    const response = await api.get<ApiEnvelope<Schedule>>(`${BASE}/${id}`);
    return response.data.data;
  },
  /** Next fire times for a saved schedule — "when will this actually run?" */
  preview: async (id: string, count = 10): Promise<SchedulePreview> => {
    const response = await api.get<ApiEnvelope<SchedulePreview>>(`${BASE}/${id}/preview`, {
      params: { count },
    });
    return response.data.data;
  },
  create: async (data: ScheduleCreateRequest): Promise<Schedule> => {
    const response = await api.post<ApiEnvelope<Schedule>>(BASE, data);
    return response.data.data;
  },
  update: async (id: string, data: ScheduleUpdateRequest): Promise<Schedule> => {
    const response = await api.put<ApiEnvelope<Schedule>>(`${BASE}/${id}`, data);
    return response.data.data;
  },
  /**
   * Pause or resume, by stating which one you want.
   *
   * The API takes the target state rather than flipping whatever is stored, and
   * it is right to: two clicks, or two operators on the same row, cannot fight
   * over it. Sending no body at all is what this did before — and the endpoint
   * requires the field, so every click was a 422 and nothing ever toggled.
   */
  toggleStatus: async (id: string, isScheduleEnabled: boolean): Promise<Schedule> => {
    const response = await api.patch<ApiEnvelope<Schedule>>(
      `${BASE}/${id}/toggle-status`, { isScheduleEnabled },
    );
    return response.data.data;
  },
  softDelete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete<ApiEnvelope<{ message: string }>>(`${BASE}/${id}`);
    return response.data.data;
  },
};
